import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebhookEndpoints, useWebhookEventTypes } from '../../hooks/useWebhookEndpoints';
import { useWebhookMutations } from '../../hooks/useWebhookMutations';
import { CreateEndpointPayload, UpdateEndpointPayload, WebhookEndpoint } from '../../models/Webhooks';
import { CreateEndpointDialog } from '../interaction/CreateEndpointDialog';
import { EndpointsTable } from '../display/EndpointsTable';
import { Spinner, usePageNavigation } from '@shared/components';

export const Main = () => {
  const { t } = useTranslation('webhooksModule');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<WebhookEndpoint | null>(null);

  const { data: endpointsData, isLoading: endpointsLoading } = useWebhookEndpoints();
  const { data: eventTypesData } = useWebhookEventTypes();
  const mutations = useWebhookMutations();

  const endpoints = endpointsData?.data ?? [];
  const events = eventTypesData?.data ?? [];
  const isFormDialogOpen = isCreateDialogOpen || editingEndpoint != null;

  const handleCreateEndpoint = (payload: CreateEndpointPayload) => {
    mutations.createEndpoint.mutate(payload, {
      onSuccess: () => setIsCreateDialogOpen(false),
    });
  };

  const handleUpdateEndpoint = (payload: UpdateEndpointPayload) => {
    if (!editingEndpoint) return;

    mutations.updateEndpoint.mutate(
      {
        id: editingEndpoint.id,
        payload: { ...payload, status: editingEndpoint.status },
      },
      { onSuccess: () => setEditingEndpoint(null) },
    );
  };

  const handleToggleStatus = (
    id: number,
    url: WebhookEndpoint['url'],
    event_types: WebhookEndpoint['event_types'],
    currentStatus: WebhookEndpoint['status'],
  ) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    mutations.updateEndpoint.mutate({ id, payload: { url, event_types, status: newStatus } });
  };

  const handleCloseFormDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingEndpoint(null);
  };

  const breadcrumbs = useMemo(() => [{ label: t('pageTitle') }], [t]);

  usePageNavigation({ breadcrumbs });

  if (endpointsLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <EndpointsTable
        endpoints={endpoints}
        onCreateClick={() => setIsCreateDialogOpen(true)}
        onEdit={setEditingEndpoint}
        onTest={(id) => mutations.testEndpoint.mutate(id)}
        onToggleStatus={handleToggleStatus}
        onDelete={(id) => mutations.deleteEndpoint.mutate(id)}
      />

      <CreateEndpointDialog
        isOpen={isFormDialogOpen}
        onClose={handleCloseFormDialog}
        onSubmit={(payload) => {
          if (editingEndpoint) {
            handleUpdateEndpoint(payload);
            return;
          }
          handleCreateEndpoint(payload as CreateEndpointPayload);
        }}
        endpoint={editingEndpoint}
        events={events}
        isLoading={mutations.createEndpoint.isPending || mutations.updateEndpoint.isPending}
      />
    </>
  );
};
